import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import checkImg from "../assets/images/check.png";
import bg from "../assets/images/bg.webp";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  content?: React.ReactNode;
  inputPlaceholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: (value?: string) => void;
  onCancel?: (value?: string) => void;
  onValueChange?: (value: string) => void;
}

// ─── Variants ────────────────────────────────────────────────────────────────

const overlayVariants: any = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.18, ease: "easeIn" } },
};

const modalVariants: any = {
  hidden: { opacity: 0, scale: 0.88, y: 28 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 24,
      mass: 0.8,
      staggerChildren: 0.07,
      delayChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.92,
    y: 16,
    transition: { duration: 0.16, ease: [0.4, 0, 1, 1] },
  },
};

const itemVariants: any = {
  hidden: { opacity: 0, y: 18, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 420, damping: 22 },
  },
};

const checkVariants: any = {
  hidden: { opacity: 0, scale: 0.4, rotate: -12 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 560,
      damping: 18,
      mass: 0.6,
    },
  },
};

const titleVariants: any = {
  hidden: { opacity: 0, y: 24, clipPath: "inset(100% 0 0 0)" },
  visible: {
    opacity: 1,
    y: 0,
    clipPath: "inset(0% 0 0 0)",
    transition: {
      type: "spring",
      stiffness: 480,
      damping: 20,
    },
  },
};

// ─── Button configs ───────────────────────────────────────────────────────────

const confirmHover: any = {
  scale: 1.035,
  boxShadow:
    "0 0 0 1.5px rgba(78,155,255,0.55), 0 0 22px rgba(30,73,116,0.7), inset 0 1px 0 rgba(255,255,255,0.12)",
  transition: { type: "spring", stiffness: 460, damping: 16 },
};

const confirmTap: any = {
  scale: 0.96,
  boxShadow: "0 0 0 1px rgba(78,155,255,0.3), 0 0 8px rgba(30,73,116,0.4)",
  transition: { type: "spring", stiffness: 600, damping: 20 },
};

const cancelHover: any = {
  scale: 1.035,
  boxShadow:
    "0 0 0 1.5px rgba(220,80,90,0.55), 0 0 22px rgba(107,42,50,0.75), inset 0 1px 0 rgba(255,255,255,0.08)",
  transition: { type: "spring", stiffness: 460, damping: 16 },
};

const cancelTap: any = {
  scale: 0.96,
  boxShadow: "0 0 0 1px rgba(220,80,90,0.3), 0 0 8px rgba(107,42,50,0.4)",
  transition: { type: "spring", stiffness: 600, damping: 20 },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  content,
  inputPlaceholder,
  initialValue = "",
  confirmLabel = "ACHIZITIONEAZA",
  cancelLabel = "ANULEAZA",
  onConfirm,
  onCancel,
  onValueChange,
}: ModalProps) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [isOpen, initialValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setValue(newVal);
    onValueChange?.(newVal);
  };

  const handleCancel = () => {
    onCancel?.(inputPlaceholder ? value : undefined);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm?.(inputPlaceholder ? value : undefined);
    onClose();
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{ backdropFilter: "blur(10px)" }}
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/30 flex-col"
        >
          {/* Background image with fade */}
          <motion.img
            src={bg}
            className="absolute select-none left-0 right-0 w-full top-8 max-h-[94%] z-[-1]"
            draggable={false}
            alt=""
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          />

          {/* Modal content */}
          <motion.div
            key="modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center z-20 max-w-[650px] w-full"
          >
            <div className="flex flex-col justify-center items-center text-center">
              {/* Check icon */}
              <motion.img
                src={checkImg}
                className="mb-3"
                variants={checkVariants}
              />

              {/* Title */}
              <motion.h2
                variants={titleVariants}
                className="uppercase text-[36px] monument-font text-white leading-[1]"
                style={{ textShadow: "0 2px 10px rgba(0,0,0,0.4)" }}
              >
                {title}
              </motion.h2>

              {/* Description */}
              <motion.p
                variants={itemVariants}
                className="text-[14px] text-[#EEF5FB]/50"
              >
                {description}
              </motion.p>
            </div>

            {/* Content */}
            <motion.p
              variants={itemVariants}
              className="text-[15px] chakra-petch-font text-[#EEF5FB] font-thin my-14 2560px:my-20"
            >
              {content}
            </motion.p>

            {/* Input + Buttons */}
            <motion.div variants={itemVariants}>
              {inputPlaceholder && (
                <motion.div
                  className="bg-gradient-to-r from-white/0 via-[#3b434e] to-white/0 relative"
                  initial={{ opacity: 0, scaleX: 0.85 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{
                    delay: 0.22,
                    type: "spring",
                    stiffness: 380,
                    damping: 22,
                  }}
                >
                  <input
                    value={value}
                    onChange={handleChange}
                    placeholder={inputPlaceholder}
                    className="text-center bebas-neue-font text-[28px] !font-[1000] text-white placeholder:text-white/40 py-4 w-full bg-transparent border-none outline-none"
                  />
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-white/0 via-white/40 to-white/0" />
                  <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-white/0 via-white/40 to-white/0" />
                </motion.div>
              )}

              <div className="grid grid-cols-2 gap-5 mt-5 bebas-neue-font">
                {/* Confirm button */}
                <motion.button
                  title="Confirm"
                  onClick={handleConfirm}
                  className="relative overflow-hidden bg-gradient-to-r from-[#1D3650] via-[#1e4974] to-[#1d3650] text-white text-[24px] p-3 rounded-md"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.28,
                      type: "spring",
                      stiffness: 420,
                      damping: 22,
                    },
                  }}
                  transition={{
                    type: "tween",
                    duration: 0.12,
                    ease: "easeOut",
                  }}
                  whileHover={confirmHover}
                  whileTap={confirmTap}
                >
                  <div
                    className="absolute left-0 top-0 w-full h-full p-[1px] pointer-events-none"
                    style={{
                      background: `
                        radial-gradient(
                          60.63% 70.9% at 50% 45%,
                          rgba(23, 133, 239, 1)    0%,
                          rgba(23, 133, 239, 0.85) 35%,
                          rgba(23, 133, 239, 0.4)  65%,
                          rgba(23, 133, 239, 0)    100%
                        ),
                        radial-gradient(
                          60.63% 50.9% at 40% 70%,
                          rgba(23, 133, 239, 0.9)  0%,
                          rgba(23, 133, 239, 0.65) 40%,
                          rgba(23, 133, 239, 0.2)  70%,
                          rgba(23, 133, 239, 0)    100%
                        )
                      `,
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />

                  <motion.span
                    className="pointer-events-none absolute inset-0"
                    initial={{ x: "-110%", skewX: "-18deg", opacity: 0 }}
                    whileHover={{
                      x: "110%",
                      opacity: [0, 0.18, 0],
                      transition: { duration: 0.45, ease: "easeInOut" },
                    }}
                    transition={{ duration: 0 }}
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
                      width: "60%",
                    }}
                  />
                  {confirmLabel}
                </motion.button>

                {/* Cancel button */}
                <motion.button
                  title="Cancel"
                  onClick={handleCancel}
                  className="relative overflow-hidden bg-gradient-to-r from-[#3d272f] via-[#6b2a32] to-[#3d272f] text-white text-[24px] p-3 rounded-md"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.34,
                      type: "spring",
                      stiffness: 420,
                      damping: 22,
                    },
                  }}
                  transition={{
                    type: "tween",
                    duration: 0.12,
                    ease: "easeOut",
                  }}
                  whileHover={cancelHover}
                  whileTap={cancelTap}
                >
                  <div
                    className="absolute left-0 top-0 w-full h-full p-[1px] pointer-events-none"
                    style={{
                      background: `
                        radial-gradient(
                          60.63% 70.9% at 50% 45%,
                          rgba(255, 39, 39, 1)    0%,
                          rgba(255, 39, 39, 0.85) 35%,
                          rgba(255, 39, 39, 0.4)  65%,
                          rgba(255, 39, 39, 0)    100%
                        ),
                        radial-gradient(
                          60.63% 50.9% at 40% 70%,
                          rgba(255, 39, 39, 0.9)  0%,
                          rgba(255, 39, 39, 0.65) 40%,
                          rgba(255, 39, 39, 0.2)  70%,
                          rgba(255, 39, 39, 0)    100%
                        )
                      `,
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />

                  <motion.span
                    className="pointer-events-none absolute inset-0"
                    initial={{ x: "-110%", skewX: "-18deg", opacity: 0 }}
                    whileHover={{
                      x: "110%",
                      opacity: [0, 0.14, 0],
                      transition: { duration: 0.45, ease: "easeInOut" },
                    }}
                    transition={{ duration: 0 }}
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,200,200,0.3), transparent)",
                      width: "60%",
                    }}
                  />
                  {cancelLabel}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
